import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // 토큰 유효성 검증
  useEffect(() => {
    if (!token) { setVerifying(false); return; }
    fetch(`/api/auth/verify-reset-token/${token}`)
      .then(r => r.json())
      .then(data => {
        setValid(data.valid);
        if (!data.valid) setError(data.error || '유효하지 않은 링크입니다.');
      })
      .catch(() => setError('네트워크 오류가 발생했습니다.'))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (password !== confirmPassword) { setError('비밀번호가 일치하지 않습니다.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      if (res.ok) {
        setSuccess(true);
        const timer = setTimeout(() => navigate('/login'), 3000);
        // cleanup on unmount handled by React (component stays mounted)
      } else {
        const data = await res.json();
        setError(data.detail || '오류가 발생했습니다.');
      }
    } catch {
      setError('네트워크 오류가 발생했습니다.');
    }
    setLoading(false);
  };

  return (
    <>
      <Header />
      <div style={{ minHeight: 'calc(100vh - 300px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
        <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px 32px' }}>
          {verifying ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#64748b', fontSize: 15 }}>링크 확인 중...</p>
            </div>
          ) : success ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#16a34a', marginBottom: 12 }}>비밀번호 변경 완료</h2>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
                새 비밀번호로 로그인해주세요.<br />3초 후 로그인 페이지로 이동합니다.
              </p>
              <Link to="/login" style={{
                display: 'inline-block', padding: '12px 32px', background: '#FF6B35', color: '#fff',
                borderRadius: 8, textDecoration: 'none', fontSize: 15, fontWeight: 600
              }}>
                로그인하기
              </Link>
            </div>
          ) : !valid ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#ef4444', marginBottom: 12 }}>링크 오류</h2>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>{error}</p>
              <Link to="/forgot-password" style={{
                display: 'inline-block', padding: '12px 32px', background: '#FF6B35', color: '#fff',
                borderRadius: 8, textDecoration: 'none', fontSize: 15, fontWeight: 600
              }}>
                다시 요청하기
              </Link>
            </div>
          ) : (
            <>
              <h2 style={{ textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>새 비밀번호 설정</h2>
              <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginBottom: 28 }}>
                새로운 비밀번호를 입력해주세요.
              </p>
              <form onSubmit={handleSubmit}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>새 비밀번호</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="6자 이상"
                  style={{
                    width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: 8,
                    fontSize: 15, outline: 'none', boxSizing: 'border-box', marginBottom: 16
                  }}
                  autoFocus
                />
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>비밀번호 확인</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="비밀번호 다시 입력"
                  style={{
                    width: '100%', padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: 8,
                    fontSize: 15, outline: 'none', boxSizing: 'border-box', marginBottom: 16
                  }}
                />
                {error && (
                  <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '14px', background: loading ? '#ccc' : '#FF6B35',
                    color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                    cursor: loading ? 'wait' : 'pointer'
                  }}
                >
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPasswordPage;
